/**
 * Enhanced Error Boundary with better fallback UI and recovery options
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, AlertTriangle, Home, Mail } from 'lucide-react';
import { logger } from '@/utils/logger';
import { EnhancedErrorHandler, EnhancedAppError, createErrorContext } from '@/utils/enhancedErrorHandler';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
  showDetails?: boolean;
  allowRetry?: boolean;
  showContactSupport?: boolean;
}

interface State {
  hasError: boolean;
  error?: EnhancedAppError;
  errorId?: string;
  retryCount: number;
}

export class EnhancedErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    retryCount: 0
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    const enhancedError = EnhancedErrorHandler.handleEnhanced(error, {
      component: 'ErrorBoundary',
      function: 'getDerivedStateFromError'
    });
    
    return { 
      hasError: true, 
      error: enhancedError,
      errorId: enhancedError.errorId
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const context = createErrorContext(
      this.props.componentName || 'ErrorBoundary',
      'componentDidCatch',
      {
        componentStack: errorInfo.componentStack,
        retryCount: this.state.retryCount
      }
    );

    const enhancedError = EnhancedErrorHandler.handleEnhanced(error, context);
    
    logger.error('React Error Boundary caught an error', {
      errorId: enhancedError.errorId,
      component: this.props.componentName || 'ErrorBoundary',
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      retryCount: this.state.retryCount
    }, error);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: undefined,
        errorId: undefined,
        retryCount: prevState.retryCount + 1
      }));
    }
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleContactSupport = () => {
    const subject = encodeURIComponent(`Erreur Application - ${this.state.errorId}`);
    const body = encodeURIComponent(
      `Bonjour,\n\nJ'ai rencontré une erreur dans l'application.\n\n` +
      `ID d'erreur: ${this.state.errorId}\n` +
      `Composant: ${this.props.componentName || 'Non spécifié'}\n` +
      `URL: ${window.location.href}\n` +
      `Heure: ${new Date().toLocaleString()}\n\n` +
      `Description du problème:\n[Veuillez décrire ce que vous faisiez quand l'erreur s'est produite]\n\n` +
      `Merci de votre aide.`
    );
    
    window.location.href = `mailto:support@example.com?subject=${subject}&body=${body}`;
  };

  public render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { title, message, actions, severity } = EnhancedErrorHandler.getEnhancedUserMessage(this.state.error);
      const canRetry = this.props.allowRetry !== false && this.state.retryCount < this.maxRetries;

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className={`h-16 w-16 ${
                  severity === 'critical' ? 'text-red-600' :
                  severity === 'high' ? 'text-red-500' :
                  severity === 'medium' ? 'text-yellow-500' :
                  'text-blue-500'
                }`} />
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">
                {title}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Error Message */}
              <Alert variant={severity === 'low' ? 'default' : 'destructive'}>
                <AlertDescription className="text-center">
                  {message}
                </AlertDescription>
              </Alert>

              {/* Recovery Actions */}
              {actions.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-muted-foreground">
                    Actions recommandées :
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    {actions.map((action, index) => (
                      <li key={index}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {canRetry && (
                  <Button
                    onClick={this.handleRetry}
                    variant="default"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Réessayer
                    {this.state.retryCount > 0 && (
                      <span className="text-xs">
                        ({this.state.retryCount}/{this.maxRetries})
                      </span>
                    )}
                  </Button>
                )}
                
                <Button
                  onClick={this.handleRefresh}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Rafraîchir la page
                </Button>
                
                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Home className="h-4 w-4" />
                  Accueil
                </Button>
                
                {this.props.showContactSupport !== false && (
                  <Button
                    onClick={this.handleContactSupport}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Contacter le support
                  </Button>
                )}
              </div>

              {/* Error Details (Development Mode) */}
              {(this.props.showDetails || import.meta.env.DEV) && this.state.error && (
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                    Détails techniques (ID: {this.state.errorId})
                  </summary>
                  <div className="mt-3 p-4 bg-muted rounded-lg">
                    <pre className="text-xs overflow-auto max-h-40 whitespace-pre-wrap">
                      {JSON.stringify({
                        errorId: this.state.error.errorId,
                        message: this.state.error.message,
                        code: this.state.error.code,
                        severity: this.state.error.severity,
                        context: this.state.error.enhancedContext,
                        retryCount: this.state.retryCount
                      }, null, 2)}
                    </pre>
                  </div>
                </details>
              )}

              {/* Retry Information */}
              {this.state.retryCount > 0 && (
                <div className="text-center text-sm text-muted-foreground">
                  Tentative {this.state.retryCount} sur {this.maxRetries}
                  {!canRetry && (
                    <div className="mt-1 text-yellow-600">
                      Nombre maximum de tentatives atteint. Veuillez rafraîchir la page.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Specialized error boundaries for different components
export const ReferentFormErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <EnhancedErrorBoundary
    componentName="ReferentForm"
    allowRetry={true}
    showContactSupport={true}
  >
    {children}
  </EnhancedErrorBoundary>
);

export const ServiceErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <EnhancedErrorBoundary
    componentName="Service"
    allowRetry={true}
    showContactSupport={true}
  >
    {children}
  </EnhancedErrorBoundary>
);

export const DashboardErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <EnhancedErrorBoundary
    componentName="Dashboard"
    allowRetry={false}
    showContactSupport={true}
  >
    {children}
  </EnhancedErrorBoundary>
);